import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./assest_design/Home.css";

import leighcheImg from "./assets/CEO.png";
import henriqueImg from "./assets/CEOH.png";
import guaranteeImg from "./assets/guarantee.png";

function Home() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title =
      "Custom Software & App Development Company in South Africa | ProCode ZA";

    const setMeta = (name, content, property = false) => {
      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;
      let tag = document.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        if (property) {
          tag.setAttribute("property", name);
        } else {
          tag.setAttribute("name", name);
        }
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMeta(
      "description",
      "ProCode ZA is a South African software and app development company building custom software, websites, dashboards, automation tools and digital platforms for businesses in Johannesburg, Cape Town, Durban and across South Africa."
    );
    setMeta(
      "keywords",
      "software development South Africa, app development South Africa, custom software development South Africa, website development South Africa, Johannesburg software company, Cape Town software developers, Durban app developers, business automation South Africa, dashboard development South Africa, ProCode ZA"
    );
    setMeta(
      "og:title",
      "Custom Software & App Development Company in South Africa | ProCode ZA",
      true
    );
    setMeta(
      "og:description",
      "Custom software, app development, automation, dashboards and digital systems built for businesses in Johannesburg, Cape Town, Durban and across South Africa.",
      true
    );
    setMeta("og:type", "website", true);
  }, []);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="home-wrapper">
      <main>
        {/* HERO */}
        <section className="hero-section hero-animated" id="top">
          <div className="hero-badge">Built in Africa. Ready for growth.</div>

          <h1>
            Custom software development for businesses that want
            to scale faster.
          </h1>

          <p>
            ProCode ZA designs high-impact websites, automation systems, AI
            tools & agents, dashboards and business platforms for startups,
            SMBs and enterprise teams in Johannesburg, Cape Town, Durban and
            across South Africa. Beyond the code, we also support branding,
            SEO, PR, marketing communication and digital growth.
          </p>

          <div className="hero-actions">
            <button
              className="cta-primary"
              onClick={() => navigate("/workspace")}
              type="button"
            >
              My Workspace
            </button>

            <button
              className="cta-outline dark-outline"
              onClick={() => scrollToSection("case-studies")}
              type="button"
            >
              Explore Case Studies
            </button>
          </div>

          <div className="floating-trust-wrapper">
            <div className="trust-pill trust-pill-light">
              Software, branding, SEO and digital growth
            </div>
            <div className="trust-pill trust-pill-dark">
              AI, IoT, automation and commercial systems
            </div>
            <div className="trust-pill trust-pill-light">
              Built for startups, SMEs and large-scale delivery
            </div>
          </div>
        </section>

        {/* VALUE & GUARANTEE */}
        <section className="value-guarantee-section reveal-up" id="support">
          <div className="value-guarantee-container">
            <div className="value-text">
              <h2>Professional software development without inflated pricing</h2>

              <p className="value-subtext">
                ProCode ZA helps startups, growing businesses and established
                teams across South Africa get access to powerful digital
                solutions without the heavy agency overhead. We focus on
                practical delivery, clear communication and software that
                creates measurable value.
              </p>

              <p className="support-text">
                Every qualifying solution can include a{" "}
                <strong>dedicated IT troubleshooting technician</strong> for
                long-term support, issue resolution and operational continuity.
              </p>

              <ul className="support-tiers">
                <li>
                  <strong>Standard Support:</strong> Ticket-based assistance for
                  core issues
                </li>
                <li>
                  <strong>Business Support:</strong> Dedicated technician with
                  virtual support
                </li>
                <li>
                  <strong>Premium Support:</strong> Priority response with
                  virtual and on-site assistance
                </li>
              </ul>

              <p className="support-disclaimer">
                Lifetime support applies to qualifying builds and support plans.
                Some low-complexity starter sites may fall outside long-term
                coverage.
              </p>
            </div>

            <div className="guarantee-visual float-gentle">
              <img
                src={guaranteeImg}
                alt="ProCode ZA software support guarantee"
              />
              <span>Long-term support for qualifying solutions</span>
            </div>
          </div>
        </section>

        {/* QUOTE */}
        <section className="section-quote reveal-up" id="quote">
          <h2>
            “Africa does not need generic systems forced into local realities.
            It needs technology designed with local context, usability and
            growth in mind.”
          </h2>
        </section>

        {/* WHY */}
        <section className="why-section reveal-up" id="about">
          <h2>Why ProCode ZA exists</h2>
          <p>
            Many businesses across Johannesburg, Cape Town, Durban and the wider
            South African market still rely on fragmented tools, manual
            processes and imported systems that do not match how teams actually
            work. That slows growth, weakens visibility and creates unnecessary
            operational friction.
          </p>

          <p>
            ProCode ZA exists to change that. We build business software,
            digital systems and modern web platforms that are tailored to the
            realities of African organisations, from lean startups to field
            teams, service businesses, education, logistics, mining and beyond.
          </p>

          <p>
            Our approach is simple: build software that works, communicates
            value clearly, and helps clients operate smarter.
          </p>
        </section>

        {/* STATS */}
        <section
          className="stats-section reveal-up"
          id="stats"
          aria-label="Business impact"
        >
          <div className="stat-card hover-lift">
            <h3>99.8%</h3>
            <p>Platform uptime on supported solutions</p>
          </div>

          <div className="stat-card hover-lift">
            <h3>40,000+</h3>
            <p>Automated tasks executed monthly</p>
          </div>

          <div className="stat-card hover-lift">
            <h3>7+</h3>
            <p>Teams currently using ProCode-built systems</p>
          </div>

          <div className="stat-card hover-lift">
            <h3>3.2x</h3>
            <p>Average operational efficiency improvement</p>
          </div>
        </section>

        {/* VISION */}
        <section className="vision-section reveal-up" id="vision">
          <h2>The ProCode vision</h2>

          <p>
            We envision an Africa where businesses, institutions and teams have
            access to world-class digital infrastructure built with local
            understanding, not copied assumptions.
          </p>

          <p>
            From custom business systems and workflow automation to websites,
            dashboards and internal platforms, we build digital tools for
            businesses in South Africa, including teams in Johannesburg, Cape
            Town and Durban that want to operate faster, clearer and better
            prepared for scale.
          </p>
        </section>

        {/* FOUNDERS */}
        <section className="founders-section reveal-up" id="founders">
          <h2>Meet the founders</h2>

          <div className="founder-highlight card-glow">
            <img
              src={leighcheImg}
              alt="Leighché Jaikarran, Co-Founder and Managing Director of ProCode ZA"
              className="founder-photo"
            />

            <div className="founder-info">
              <h3>Leighché Jaikarran</h3>
              <span className="founder-role">
                Co-Founder · Managing Director · Business, Technology & AI Solutions
              </span>

              <p>
                Leighché drives the strategic vision behind ProCode ZA. With a
                background spanning software development, applied research,
                invention and digital problem-solving, the focus is always on
                building technology that is practical, accessible and relevant
                to African businesses.
              </p>

              <p>
                <strong>
                  “When Africa builds its own systems, it builds its own
                  leverage.”
                </strong>
              </p>
            </div>
          </div>

          <div className="founder-highlight reverse card-glow">
            <img
              src={henriqueImg}
              alt="Henriqué Barcelos Jr, Co-Founder and Executive Operations at ProCode ZA"
              className="founder-photo"
            />

            <div className="founder-info">
              <h3>Henriqué Barcelos Jr</h3>
              <span className="founder-role">
                Co-Founder · Executive Director · Strategy, Relations & Impact
              </span>

              <p>
                Henriqué helps turn strong software ideas into operational
                impact. With experience in communications, workflow design and
                real-world implementation, he ensures ProCode ZA solutions are
                not only well-built, but well-adopted.
              </p>

              <p>
                <strong>
                  “Technology only creates value when people trust it, use it
                  and benefit from it.”
                </strong>
              </p>
            </div>
          </div>
        </section>

        {/* VALUES */}
        <section className="values-section reveal-up" id="values">
          <h2>What we believe in</h2>

          <div className="values-grid">
            <div className="value-box hover-lift">
              <h3>Human-centred systems</h3>
              <p>
                Good software should feel intuitive, clear and empowering for
                real users.
              </p>
            </div>

            <div className="value-box hover-lift">
              <h3>Practical innovation</h3>
              <p>
                We build for real business problems, not trends that look good
                but solve little.
              </p>
            </div>

            <div className="value-box hover-lift">
              <h3>African operating context</h3>
              <p>
                Our systems are shaped around local realities, constraints and
                opportunities.
              </p>
            </div>

            <div className="value-box hover-lift">
              <h3>Reliability and scale</h3>
              <p>
                Every build should remain stable, adaptable and ready to grow
                with the client.
              </p>
            </div>
          </div>
        </section>

        {/* CASE STUDIES */}
        <section className="case-section reveal-up" id="case-studies">
          <div className="section-heading-wrap">
            <h2>What we build across software, AI, branding and emerging technology</h2>
            <p>
              ProCode ZA delivers more than websites and software. We build
              digital, technical and creative solutions for small businesses,
              growing brands, institutions and larger commercial operations
              across South Africa and the wider African market.
            </p>
          </div>

          <div className="case-grid">
            <div className="case-card hover-lift" id="software-systems">
              <h3>Custom software and business systems</h3>
              <p>
                Internal platforms, admin systems, portals, workflow tools and
                operational software designed around how your team actually
                works.
              </p>
              <span className="case-tag">Systems · Portals · Operations</span>
            </div>

            <div className="case-card hover-lift" id="web-branding">
              <h3>Websites, branding and creative development</h3>
              <p>
                Premium websites, visual branding, digital assets, campaign
                content and creative work that helps businesses look credible
                and convert better.
              </p>
              <span className="case-tag">Web · Branding · Creative</span>
            </div>

            <div className="case-card hover-lift" id="ai-ml">
              <h3>AI, automation and machine learning</h3>
              <p>
                Smart automation tools, AI-powered workflows, model training
                support, data-driven products and machine learning solutions for
                practical use.
              </p>
              <span className="case-tag">AI · ML · Automation</span>
            </div>

            <div className="case-card hover-lift" id="iot-robotics">
              <h3>IoT, robotics and intelligent hardware</h3>
              <p>
                Commercial and experimental builds involving sensors, smart
                devices, embedded systems, robotics concepts and connected
                technologies.
              </p>
              <span className="case-tag">IoT · Robotics · Hardware</span>
            </div>

            <div className="case-card hover-lift" id="education-training">
              <h3>Education and training solutions</h3>
              <p>
                Educational platforms, training systems, learning support tools
                and technology solutions built for institutions, programmes and
                communities.
              </p>
              <span className="case-tag">Education · Training · Platforms</span>
            </div>

            <div className="case-card hover-lift" id="scale-delivery">
              <h3>Large-scale coordination and delivery support</h3>
              <p>
                With creative, technical and programme support capacity, we can
                assist with structured rollouts, coordinated projects and
                multi-part solution delivery.
              </p>
              <span className="case-tag">Programme Support · Scale · Delivery</span>
            </div>
          </div>
        </section>

        {/* COST ESTIMATOR */}
        <section className="cost-estimator-section reveal-up" id="pricing">
          <div className="cost-cta-card">
            <h2>Estimate your software project cost in minutes</h2>

            <p className="cost-cta-text">
              Not sure what your website, system or automation project should
              cost? Use <strong>ProQuote</strong> to get a clear, transparent
              estimate based on your actual needs.
            </p>

            <div className="cost-highlight">
              <span>Projects start from</span>
              <h3>R500+</h3>
            </div>

            <p className="referral-highlight">
              <strong>Recommended by someone?</strong> Get{" "}
              <strong>10% off</strong> qualifying projects when you tell us who
              referred you.
            </p>

            <div className="hero-actions center-actions">
              <button
                className="cta-primary"
                onClick={() => navigate("/proquote")}
                type="button"
              >
                Try ProQuote
              </button>

              <button
                className="cta-outline light-outline"
                onClick={() => navigate("/contact")}
                type="button"
              >
                Speak to a Specialist
              </button>
            </div>

            <p className="cost-disclaimer">
              Final pricing depends on complexity, features, integrations and
              support level.
            </p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="final-cta reveal-up" id="contact">
          <h2>Let’s build something that actually moves your business forward.</h2>
          <p>
            Whether you need a business website, internal platform, dashboard,
            automation tool or custom software solution, ProCode ZA is ready to
            help you build with confidence.
          </p>

          <div className="hero-actions center-actions">
            <button
              className="cta-primary"
              onClick={() => navigate("/start-project")}
              type="button"
            >
              Start a Project
            </button>

            <button
              className="cta-outline dark-outline"
              onClick={() => navigate("/contact")}
              type="button"
            >
              Contact Us
            </button>
          </div>
        </section>
      </main>

      <footer className="footer">
        <p>
          © {new Date().getFullYear()} ProCode ZA · Custom software and app
          development in Johannesburg, Cape Town, Durban and across South
          Africa, built with purpose for Africa.
        </p>
      </footer>
    </div>
  );
}

export default Home;